import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createMemberApi,
  deleteMemberApi,
  getMemberByIdApi,
  getMembersApi,
  updateMemberApi,
} from '../-api/api';
import type { Member, MembersQueryParams } from '../-api/types';

const MEMBERS_QUERY_KEY = ['members'] as const;

export function useMembers(orgUrn: string, params: MembersQueryParams = {}) {
  return useQuery({
    queryKey: [...MEMBERS_QUERY_KEY, orgUrn, params],
    queryFn: () => getMembersApi(orgUrn, params),
    enabled: !!orgUrn,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useMember(orgUrn: string, id: string) {
  return useQuery({
    queryKey: [...MEMBERS_QUERY_KEY, orgUrn, 'detail', id],
    queryFn: () => getMemberByIdApi(orgUrn, id),
    enabled: !!orgUrn && !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useCreateMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      orgUrn,
      data,
    }: {
      orgUrn: string;
      data: Omit<Member, 'urn' | 'created_at' | 'updated_at'>;
    }) => createMemberApi(orgUrn, data),
    onSuccess: (_, { orgUrn }) => {
      queryClient.invalidateQueries({
        queryKey: [...MEMBERS_QUERY_KEY, orgUrn],
      });
    },
  });
}

export function useUpdateMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      orgUrn,
      id,
      data,
    }: {
      orgUrn: string;
      id: string;
      data: Partial<Omit<Member, 'urn' | 'created_at' | 'updated_at'>>;
    }) => updateMemberApi(orgUrn, id, data),
    onSuccess: (_, { orgUrn, id }) => {
      queryClient.invalidateQueries({
        queryKey: [...MEMBERS_QUERY_KEY, orgUrn],
      });
      queryClient.invalidateQueries({
        queryKey: [...MEMBERS_QUERY_KEY, orgUrn, 'detail', id],
      });
    },
  });
}

export function useDeleteMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orgUrn, id }: { orgUrn: string; id: string }) =>
      deleteMemberApi(orgUrn, id),
    onSuccess: (_, { orgUrn }) => {
      queryClient.invalidateQueries({
        queryKey: [...MEMBERS_QUERY_KEY, orgUrn],
      });
    },
  });
}
